import React from 'react';
import { Box, HStack, VStack, Text, Badge, List, ListItem } from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : null;
};

/**
 * What each expert said about an ayah, translation or recitation: their vote, suggested
 * correction and comment. `correctionFont` styles suggested corrections, e.g. "QuranFont"
 * for Arabic text, which is then shown right to left.
 *
 * Falls back to the plain comment list for responses from before expert_feedback existed.
 */
const ExpertFeedback = ({ summary, correctionFont }) => {
  const feedback = summary?.expert_feedback;
  const comments = summary?.expert_comments || [];
  if (!feedback?.length && !comments.length) return null;

  return (
    <Box p={3} bg="yellow.50" borderRadius="md" borderLeft="4px" borderColor="yellow.400">
      <HStack mb={3}>
        <WarningIcon color="yellow.600" />
        <Text fontWeight="bold" color="yellow.800">Expert Comments & Corrections</Text>
      </HStack>

      {feedback?.length ? (
        <VStack align="stretch" spacing={3}>
          {feedback.map((entry, idx) => {
            const rejected = entry.verification_status === false;
            const date = formatDate(entry.verified_at);
            return (
              <Box key={`${entry.user_id}-${idx}`} p={3} bg="white" borderRadius="md" borderWidth="1px" borderColor="yellow.200">
                <HStack justify="space-between" mb={2} flexWrap="wrap" spacing={2}>
                  <HStack spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold">{entry.username || `Expert #${idx + 1}`}</Text>
                    <Badge colorScheme={rejected ? 'red' : 'green'} fontSize="2xs">
                      {rejected ? 'Rejected' : 'Verified'}
                    </Badge>
                  </HStack>
                  {date && <Text fontSize="xs" color="gray.500">{date}</Text>}
                </HStack>

                {entry.corrections && (
                  <Box mb={entry.comments ? 2 : 0}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>Suggested correction</Text>
                    <Text
                      p={2} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.100"
                      {...(correctionFont
                        ? { fontFamily: correctionFont, fontSize: 'xl', dir: 'rtl', textAlign: 'right' }
                        : { fontSize: 'sm', dir: 'auto' })}
                    >
                      {entry.corrections}
                    </Text>
                  </Box>
                )}

                {entry.comments && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>Comment</Text>
                    <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{entry.comments}</Text>
                  </Box>
                )}

                {!entry.corrections && !entry.comments && (
                  <Text fontSize="sm" color="gray.500">Rejected without a comment or correction.</Text>
                )}
              </Box>
            );
          })}
        </VStack>
      ) : (
        <List spacing={2}>
          {comments.map((comment, idx) => (
            <ListItem key={idx} fontSize="sm" color="gray.700">• {comment}</ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default ExpertFeedback;
